import { useState, useEffect } from 'react';
import { useAuth } from '@/app/hooks/context/AuthContext';
import { ProfileHeader } from "@/app/components/Navbar/Profile/ProfileHeader";
import { TabNavigation, ProfileTab } from "@/app/components/Navbar/Profile/TabNavigation";
import { OverviewTab } from "@/app/components/Navbar/Profile/OverviewTab";
import { OrdersTab } from "@/app/components/Navbar/Profile/OrdersTab";
import { HistoryTab } from "@/app/components/Navbar/Profile/HistoryTab";
import { RewardsTab } from "@/app/components/Navbar/Profile/RewardsTab";
import { StatisticsTab } from "@/app/components/Navbar/Profile/StatisticsTab";
import { User } from "@/types/user";

interface ProfileModalProps {
    user: User;
    onClose: () => void;
}

export default function ProfileModal({ user, onClose }: ProfileModalProps) {
    const { isKickProvider, logout } = useAuth();
    const [activeTab, setActiveTab] = useState<ProfileTab>('overview');

    // Prevent body scroll when modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    return (
        <div
            className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="bg-gray-900 rounded-2xl w-full max-w-4xl overflow-hidden max-h-[calc(100dvh-2rem)] sm:max-h-[85vh] flex flex-col my-4 sm:my-8 shadow-2xl shadow-black/50 ring-1 ring-white/5"
                onClick={e => e.stopPropagation()}
            >
                <ProfileHeader user={user} onClose={onClose} />

                {/* ---------- Sticky, scrollable tab bar (mobile friendly) ---------- */}
                {isKickProvider && (
                    <div
                        className="sticky top-0 z-30 bg-gray-900 shrink-0"
                    /* sticky ensures the tab bar stays visible while content scrolls */
                    >
                        <div className="overflow-x-auto scrollbar-none">
                            {/* min-w-max prevents wrapping so horizontal scroll works on mobile */}
                            <div className="flex min-w-max px-4 sm:px-6 pt-3 pb-1">
                                <TabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
                            </div>
                        </div>
                    </div>
                )}

                {/* ---------- Content area ---------- */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 scrollbar-thin scrollbar-thumb-sky-700 scrollbar-track-sky-300">
                    {activeTab === 'overview' && (
                        <OverviewTab
                            user={user}
                            onLogout={() => {
                                onClose();
                                logout();
                            }}
                            isKickProvider={isKickProvider}
                        />
                    )}

                    {isKickProvider && (
                        <>
                            {activeTab === 'stats' && <StatisticsTab />}
                            {activeTab === 'orders' && <OrdersTab />}
                            {activeTab === 'history' && <HistoryTab />}
                            {activeTab === 'rewards' && (
                                <RewardsTab user={user} isKickProvider={isKickProvider} />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}